import { useState, type FormEvent } from 'react';
import { useCreateTask } from '../api/hooks';
import type { Department, Employee, ShiftLocation } from '../api/types';
import { useLang } from '../i18n/LangProvider';
import { toast } from '../ui/toast';
import { Icon } from '../ui/Icon';

/** Where a new task lands. Offices and departments accept several at once. */
type Dest = 'me' | 'office' | 'dept' | 'person';

const DESTS: { id: Dest; label: string; icon: 'user' | 'map' | 'users' | 'at' }[] = [
  { id: 'me', label: 'A me', icon: 'user' },
  { id: 'office', label: 'Ufficio', icon: 'map' },
  { id: 'dept', label: 'Reparto', icon: 'users' },
  { id: 'person', label: 'Persona', icon: 'at' },
];

const WHENS = [
  { id: '', label: 'Una volta' },
  { id: 'daily', label: 'Ogni giorno' },
  { id: 'weekdays', label: 'Lun–Ven' },
  { id: 'weekly:1', label: 'Ogni lunedì' },
  { id: 'weekly:5', label: 'Ogni venerdì' },
];

const PRIORITIES = [
  { id: 'bassa', label: 'Bassa' },
  { id: 'media', label: 'Media' },
  { id: 'alta', label: 'Alta' },
];

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Composer for the daily checklists an office or a department runs on.
 *
 * The destination comes first because it changes what the task *is*: one row of
 * chips per kind, several targets at a time, and a plain-language summary of
 * what is about to be created before you commit to it.
 */
export function TaskComposer({ offices, departments, employees, meId }: {
  offices: ShiftLocation[]; departments: Department[]; employees: Employee[]; meId: string;
}) {
  const { t } = useLang();
  const create = useCreateTask();

  const [title, setTitle] = useState('');
  const [dest, setDest] = useState<Dest>('me');
  const [targets, setTargets] = useState<string[]>([]);
  const [personId, setPersonId] = useState('');
  const [when, setWhen] = useState('');
  const [time, setTime] = useState('');
  const [priority, setPriority] = useState('media');
  const [open, setOpen] = useState(false);

  const pickDest = (d: Dest) => {
    setDest(d);
    setTargets([]);
    setOpen(d === 'office' || d === 'dept' || d === 'person' ? true : open);
  };
  const toggleTarget = (key: string) =>
    setTargets((list) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]));

  /** One task per chosen target; "me"/"person" always produce exactly one. */
  const recipients = dest === 'office' || dest === 'dept' ? targets : [dest === 'person' ? personId : meId];
  const ready = title.trim().length > 0 && recipients.every(Boolean) && recipients.length > 0;

  const summary = () => {
    const what = when ? t(WHENS.find((w) => w.id === when)!.label) : t('Una volta');
    const at = time ? ` ${t('alle')} ${time}` : '';
    const who = dest === 'office'
      ? targets.map((k) => offices.find((o) => o.key === k)?.short ?? k.toUpperCase()).join(', ')
      : dest === 'dept'
        ? targets.map((k) => departments.find((d) => d.id === k)?.name ?? k).join(', ')
        : dest === 'person'
          ? (employees.find((e) => e.id === personId)?.name ?? '')
          : t('me');
    return `${what}${at} · ${who || t('nessun destinatario')}`;
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    // A time without a date means "today at that hour" — the natural reading for
    // an opening or closing routine.
    const dueAt = time ? new Date(`${iso(new Date())}T${time}:00`).toISOString() : undefined;
    const base = { title: title.trim(), recurrence: when || undefined, priority, dueAt };
    try {
      for (const key of recipients) {
        const target = dest === 'office' ? { officeKey: key }
          : dest === 'dept' ? { departmentId: key }
          : { assigneeId: key };
        await create.mutateAsync({ ...base, ...target });
      }
      toast.success(recipients.length === 1 ? t('Task creato.') : t('{n} task creati.', { n: recipients.length }));
      setTitle('');
    } catch {
      toast.error(t('Creazione del task non riuscita.'));
    }
  }

  return (
    <form className="card tcp" onSubmit={submit}>
      <div className="tcp__row tcp__row--main">
        <input
          className="input tcp__title" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder={t('Nuovo task… (es. Apertura cassa)')} aria-label={t('Titolo del task')}
          onFocus={() => setOpen(true)}
        />
        <button type="button" className="btn btn--sm tcp__toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <Icon name={open ? 'chevU' : 'chevD'} size={13} /> {t('Opzioni')}
        </button>
        <button className="btn btn--primary tcp__submit" type="submit" disabled={!ready || create.isPending}>
          {create.isPending ? t('Creazione…') : recipients.length > 1 ? t('Crea {n}', { n: recipients.length }) : t('Aggiungi')}
        </button>
      </div>

      {open && (
        <>
          <div className="tcp__row">
            <span className="tcp__label">{t('Per')}</span>
            <div className="tcp__chips">
              {DESTS.map((d) => (
                <button key={d.id} type="button" className={`tcp__chip${dest === d.id ? ' is-on' : ''}`} onClick={() => pickDest(d.id)}>
                  <Icon name={d.icon} size={12} /> {t(d.label)}
                </button>
              ))}
            </div>
          </div>

          {dest === 'office' && (
            <div className="tcp__row">
              <span className="tcp__label">{t('Sedi')}</span>
              <div className="tcp__chips">
                {offices.map((o) => (
                  <button key={o.key} type="button" className={`tcp__chip${targets.includes(o.key) ? ' is-on' : ''}`} onClick={() => toggleTarget(o.key)}>
                    {targets.includes(o.key) && <Icon name="check" size={11} />} {o.short ?? o.key.toUpperCase()}
                  </button>
                ))}
                {offices.length > 1 && (
                  <button type="button" className="tcp__all" onClick={() => setTargets(targets.length === offices.length ? [] : offices.map((o) => o.key))}>
                    {targets.length === offices.length ? t('Nessuna') : t('Tutte')}
                  </button>
                )}
              </div>
            </div>
          )}

          {dest === 'dept' && (
            <div className="tcp__row">
              <span className="tcp__label">{t('Reparti')}</span>
              <div className="tcp__chips">
                {departments.map((d) => (
                  <button key={d.id} type="button" className={`tcp__chip${targets.includes(d.id) ? ' is-on' : ''}`} onClick={() => toggleTarget(d.id)}>
                    {targets.includes(d.id) && <Icon name="check" size={11} />} {d.short ?? d.name}
                  </button>
                ))}
                {departments.length > 1 && (
                  <button type="button" className="tcp__all" onClick={() => setTargets(targets.length === departments.length ? [] : departments.map((d) => d.id))}>
                    {targets.length === departments.length ? t('Nessuno') : t('Tutti')}
                  </button>
                )}
              </div>
            </div>
          )}

          {dest === 'person' && (
            <div className="tcp__row">
              <span className="tcp__label">{t('Chi')}</span>
              <select className="input tcp__person" value={personId} onChange={(e) => setPersonId(e.target.value)}>
                <option value="">{t('— scegli —')}</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
          )}

          <div className="tcp__row">
            <span className="tcp__label">{t('Quando')}</span>
            <div className="tcp__chips">
              {WHENS.map((w) => (
                <button key={w.id} type="button" className={`tcp__chip${when === w.id ? ' is-on' : ''}`} onClick={() => setWhen(w.id)}>
                  {w.id && <Icon name="repeat" size={11} />} {t(w.label)}
                </button>
              ))}
              <input className="input tcp__time" type="time" value={time} onChange={(e) => setTime(e.target.value)}
                     aria-label={t('Ora')} title={t('Ora entro cui va fatto')} />
            </div>
          </div>

          <div className="tcp__row">
            <span className="tcp__label">{t('Priorità')}</span>
            <div className="tcp__chips">
              {PRIORITIES.map((p) => (
                <button key={p.id} type="button" className={`tcp__chip tcp__chip--${p.id}${priority === p.id ? ' is-on' : ''}`} onClick={() => setPriority(p.id)}>
                  {t(p.label)}
                </button>
              ))}
            </div>
          </div>

          <p className="tcp__summary muted">
            <Icon name="info" size={12} /> {summary()}
            {when && <> · <span className="tcp__note">{t('alla spunta si riapre sul giorno successivo')}</span></>}
          </p>
        </>
      )}
    </form>
  );
}
